import { useState, useEffect } from 'react';
import { IcoClose, IcoCheck } from './common/Icons.jsx';
import { normSeats, nombreDe } from '../store/cartStore.js';
import styles from './MoveTabSheet.module.css';
import { useAtrasCierra } from '../lib/atras.js';

/**
 * De quién es un platillo.
 *
 * Se abre al tocar el comensal de una línea. Un toque en una persona la hace
 * dueña y cierra la hoja: es lo que pasa casi siempre y cuesta lo mismo que
 * antes costaba el chip que rotaba —pero sin que la línea salte de grupo bajo
 * el dedo y sin gestos escondidos—. "Lo comparten varios" cambia a casillas
 * con Guardar, para la botella que pidieron dos de los cuatro.
 */
export default function SeatsSheet({ open, linea, seats = [], comensales = [], onClose, onAgregar, onGuardar }) {
  useAtrasCierra(open, onClose);
  const [sel, setSel] = useState([]);
  const [varios, setVarios] = useState(false);

  useEffect(() => {
    if (!open) return;
    const actuales = normSeats(linea?.seats);
    setSel(actuales);
    // Si ya venía compartido se abre en el modo que lo entiende.
    setVarios(actuales.length > 1);
  }, [open, linea]);

  if (!open || !linea) return null;

  const guardarYcerrar = (lista) => { onGuardar(lista); onClose(); };

  const tocar = (n) => {
    if (!varios) { guardarYcerrar([n]); return; }
    setSel((prev) => (prev.includes(n) ? prev.filter((x) => x !== n) : normSeats([...prev, n])));
  };

  const sentarOtro = () => {
    const n = onAgregar?.();
    if (!n) return;
    if (!varios) { guardarYcerrar([n]); return; }
    setSel((p) => normSeats([...p, n]));
  };

  return (
    <div className={styles.scrim} role="presentation" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className={styles.sheet} role="dialog" aria-modal="true">
        <div className={styles.head}>
          <div>
            <h2 className={styles.title}>{varios ? '¿Entre quiénes?' : '¿De quién es?'}</h2>
            <p className={styles.sub}>{linea.product?.name}</p>
          </div>
          <button className={styles.close} onClick={onClose} aria-label="Cerrar"><IcoClose size={16} /></button>
        </div>

        <div className={styles.chipsGrid}>
          <button
            className={`${styles.chipBig} ${!sel.length && varios ? styles.chipBigOn : ''}`}
            onClick={() => (varios ? setSel([]) : guardarYcerrar([]))}
          >
            Mesa<small>se reparte</small>
          </button>
          {seats.map((n) => {
            const puesto = sel.includes(n);
            return (
              <button
                key={n}
                className={`${styles.chipBig} ${puesto ? styles.chipBigOn : ''}`}
                onClick={() => tocar(n)}
                aria-pressed={puesto}
              >
                {nombreDe(n, comensales)}
                {varios && puesto && <small><IcoCheck size={12} /></small>}
              </button>
            );
          })}
          {onAgregar && (
            <button className={styles.chipBigGhost} onClick={sentarOtro}>+ comensal</button>
          )}
        </div>

        <button
          className={`${styles.alternar} ${varios ? styles.alternarOn : ''}`}
          onClick={() => setVarios((v) => !v)}
          aria-pressed={varios}
        >
          {varios ? '✓ Lo comparten varios' : 'Lo comparten varios…'}
        </button>

        {varios && (
          <>
            <p className={styles.aviso}>
              {sel.length === 0
                ? 'Se repartirá entre todos los comensales de la mesa.'
                : sel.length === 1
                  ? `Lo paga ${nombreDe(sel[0], comensales)}.`
                  : `Se reparte en partes iguales entre ${sel.length} comensales.`}
            </p>
            <div className={styles.acciones}>
              <button className="btn btn--ghost" onClick={onClose}>Cancelar</button>
              <button className="btn btn--primary" onClick={() => guardarYcerrar(sel)}>Guardar</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
